/**
 * 新版本登录库
 * @author colynchen
 * @created 2019-03-20
 */
import { invoke, on } from '@tencent/vft-atom/lib/tenvideo';
import cookie, { parseCookieString } from '@tencent/vft-common/cookie';
import { isNeedUseCookieProxy } from '@tencent/vft-common/CookieProxy';
import env from '@tencent/vft-common/env';
import { getUserAgent, globalObject } from '@tencent/vft-common/system';
import { delQueStr, filterXSS, getUrlParam, hasOwn } from '@tencent/vft-common/util.esm';

import LoginProxy from './core/LoginProxy';
import {
  checkFamilyChildLogin,
  checkUser,
  getAvatar,
  getFamilyRole,
  getLoginInfo,
  getMainCookie,
  getMainUserInfo,
  getNick,
  getToken,
  getUid,
  getUin,
  getVuid,
  isFamilyChildLogin,
  isLogin,
  isNewQQLogin,
  isPhoneLogin,
  isPTLogin,
  isQQLogin,
  isStrictLogin,
  isWXLogin,
  logout,
  openLogin,
  setConfig,
} from './index.esm';
import {
  buildCSRF,
  clearCookie,
  innerToken2tenvideo,
  loginType,
  refreshAuthToken,
  syncCookies,
} from './lib';
import { COOKIE_EXPIRED_TIME, LOGIN_TYPE, LOGIN_VERSION } from './utils/config';
import { clearErrorCookies, clearPageCookies, clearRootDomainCookies, clearVideoDomainCookies, generateGuid, getDomain, isNativeLogin } from './utils/helper';
import { handleMqqLogin, requestQQLogin } from './utils/MqqHelper';
import * as reporter from './utils/report';

const ON_APP_LOGIN = 'onActionLoginFinish';
const ON_APP_LOGOUT = 'onActionLogoutFinish';
const ON_APP_SWITCH_LOGIN = 'onSwitchLoginStateResponse';
const APP_LOGIN_CHANGED_EVENTS = [ON_APP_LOGIN, ON_APP_LOGOUT, ON_APP_SWITCH_LOGIN];
const UA = getUserAgent();

let isInitCall = false;
let isInitReady = false;

if (globalObject?.addEventListener) {
  globalObject.addEventListener('load', () => {
    setTimeout(() => {
      if (!isInitCall) {
        console && console.warn('`login.init()` should be called before your business logic, in case something goes wrong.');
      }
    }, 0);
  });
}

// 初始化回调队列
const ONREADY_QUEUES = [];
let initStartTime = 0;
const setReady = function () {
  if (ONREADY_QUEUES.length > 0) {
    ONREADY_QUEUES.forEach((fn) => {
      if (typeof fn === 'function') {
        fn();
      }
    });
    ONREADY_QUEUES.length = 0;
  }
  isInitReady = true;
  reporter.report({
    f_event: reporter.EVENT_INIT_READY,
    f_cost: Date.now() - initStartTime,
  });
};

/**
 * 登录库初始化
 * @param {Function} callback 初始化完成回调
 */
function init(callback) {
  if (isInitReady) {
    return typeof callback === 'function' && callback();
  }

  ONREADY_QUEUES.push(callback);

  // 避免重复调用 init 方法
  if (isInitCall) return;

  // 初始化上报
  reporter.report({
    f_event: reporter.EVENT_INIT_START,
  });

  isInitCall = true;
  initStartTime = Date.now();
  // APP 环境下同步 cookie，但不做任何处理
  if (isNeedUseCookieProxy()) {
    cookie.enableProxy();
    syncAppCookies().then(setReady);
    APP_LOGIN_CHANGED_EVENTS.forEach((e) => {
      on(e, () => {
        syncAppCookies();
      });
    });
    return;
  }

  if (env.isApp) {
    // 腾讯视频应用 种入deviceInfo, 安全中台需要这些数据
    invoke('getDeviceInfo', null, { timeout: 500 }).then((info) => {
      const whiteKeys = ['deviceModel', 'systemVersion', 'turing_ticket', 'deviceName', 'manufacturer'];
      Object.keys(info || {}).filter(key => whiteKeys.includes(key))
        .forEach((key) => {
          cookie.set(key, info[key], {
            domain: 'qq.com',
            path: '/',
          });
        });
      setReady();
    })
      .catch(setReady);
    return;
  }

  // 背景: QQ浏览器11.6.5版本开始支持登录态打通
  // 仅支持在长视频播放场景种视频票据到qq.com, 其他场景登录不会种视频票据,
  // 但票据只要换出来后是全局共享的,会导致搜索场景访问的H5页面登录态污染,无法正常登录
  // !!优先判断非高级框架场景，清除终端种的票据
  if (env.isMqb && !env.isTenvideoUnion) {
    clearRootDomainCookies();
  }

  // 手Q插件，如果是QQ互联登录态的，由客户端保证cookie的正确性，不需要续期票据
  // 如票据有问题，清除票据也会导致页面无登录态，所以js不做处理
  if (env.isMqqPlugin && isNewQQLogin()) {
    setReady();
    return;
  }

  // 内容分发登录态打通场景，默认清掉当前页面cookie信息，避免登录态污染
  // 手q内无法重新登录不处理
  if (isNativeLogin() && !env.isMqq) {
    clearPageCookies();
    // iframe 场景调用后台 auth_logout 接口会导致在 iframe 内取到空 cookie
    if (top !== self) {
      setReady();
      return;
    }
    // 清除 video.qq.com 域下的 cookie，避免与客户端种的重复
    return clearVideoDomainCookies().then(setReady);
  }

  // 存在有效的mpcookie，认为是来自于腾讯视频小程序，需要同步小程序的登录态
  // 其他小程序不做登录态同步，直接走h5逻辑
  const mpCookie = decodeURIComponent(getUrlParam('mpcookie'));
  if (mpCookie) {
    if (env.isWeChatMiniProgram) {
      const hasSync = syncWechatCookie(mpCookie);
      if (hasSync) return;
    } if (env.isBaiduMiniProgram) {
      syncBaiduCookie(mpCookie);
      return;
    }
  }

  if (UA.match(/(weishi|msdk|qqsports|qqmusic)/i) || getUrlParam('msdkEncodeParam').length > 0) {
    // 支持微视、腾讯体育、游戏走票据转换
    // 第三方场景无须续期票据
    innerToken2tenvideo(() => {
      syncCookies(setReady);
    });
  } else {
    // 移除 qq.com 域下的微信登录态
    // 来源游戏业务的 cookie 污染，特坑！！！
    clearRootDomainCookies();
    clearPageCookies();
    clearErrorCookies();
    // 同步 cookie 信息
    syncCookies(() => {
      /*
       * 手Q中(除手Q插件场景)使用静默授权获取QQ互联登录态
       */
      if (env.isMqq && !env.isMqqPlugin && !isPhoneLogin()) {
        // 为了避免qq互联和ptlogin票据同时存在可能导致的登录类型优先级混乱，把uin&skey清除掉
        if (isNewQQLogin()) {
          // 已登录且在5分钟内，无需重复续期
          if (!needRefreshAuthToken()) {
            return setReady();
          }
          refreshAuthToken((err) => {
            // 手Q中如果在隐私 -> 授权管理中手动移除"腾讯视频H5"的授权，会导致QQ互联票据失效，从而刷票失败，这时需要重试静默授权一次
            if (err?.code === 1112) { // 无效的QQ票据
              reporter.report({
                f_event: reporter.EVENT_MQQ_NATIVE_LOGIN_REFRESH_FAIL,
              });
              handleMqqLogin().then(setReady)
                .catch(setReady);
            } else {
              syncCookies(setReady);
            }
          });
        } else {
          handleMqqLogin().then(setReady)
            .catch(setReady);
        }
      } else {
        // 以下情况均为无效登录态，需要清除登录态信息
        // 1. 没有QQ或微信登录信息，但有视频票据
        // 2. 是QQ互联登录态，也有旧的视频票据，会导致登录态混乱
        // 3. 当前是微信登录态且存在互联登录态时
        if ((!isLogin() && (cookie.get('vuserid') || cookie.get('vusession')))
          || (isWXLogin() && (cookie.get('vqq_vuserid') || cookie.get('vqq_vusession')))
          || (isNewQQLogin() && cookie.get('vuserid') && cookie.get('vusession'))
        ) {
          clearCookie(() => {
            syncCookies(setReady);
          });
        } else if (isLogin()) {
          // 已经登录，则续期票据
          if (needRefreshAuthToken()) { // 5分钟/刷新票据
            // 续期后需要再次同步 cookie 信息
            refreshAuthToken(() => {
              syncCookies(setReady);
            });
          } else {
            setReady();
          }
        } else {
          setReady();
        }
      }
    });
  }
}

// 微信小程序同步登录态
function syncWechatCookie(mpCookie) {
  const cookieParam = parseCookieString(filterXSS(mpCookie));
  const setLoginType = cookieParam && cookieParam.main_login;
  if (setLoginType && env.isWeChatMiniProgram) {
    if (cookieParam.main_login === LOGIN_TYPE.wx) {
      const ru = delQueStr(location.href, 'mpcookie');
      location.href = `https://film.qq.com/weixin/login.html?type=wx&ru=${encodeURIComponent(ru)}`;
      return true;
    }
    clearRootDomainCookies();

    clearCookie(setLoginType, () => {
      Object.keys(cookieParam).forEach((key) => {
        cookie.set(key, cookieParam[key], {
          domain: 'qq.com',
          path: '/',
        });
      });

      refreshAuthToken(setLoginType, () => {
        Object.keys(cookieParam).forEach((key) => {
          cookie.remove(key, {
            domain: 'qq.com',
            path: '/',
          });
        });
        syncCookies(setReady);
      });
    });
    return true;
  }
  return false;
}

// 百度app小程序同步登录态
function syncBaiduCookie(mpCookie) {
  if (!cookie.get('guid')) {
    cookie.set('guid', generateGuid(), {
      domain: 'qq.com',
      path: '/',
    });
  }
  const cookieOption = {
    expires: COOKIE_EXPIRED_TIME,
    domain: getDomain(),
    path: '/',
  };
  const cookieParam = parseCookieString(filterXSS(mpCookie));
  const {
    vqq_access_token: vqqAccessToken,
    vqq_openid: vqqOpenid,
    vqq_appid: vqqAppid,
    qq_nick: vqqNick,
    qq_head: vqqHead,
  } = cookieParam;
  requestQQLogin({
    access_token: vqqAccessToken,
    openid: vqqOpenid,
    appid: vqqAppid,
  }).then(() => {
    // 开发环境域名有多个，清除film.video.qq.com上次写入的cookie，最后读取当前域名下的用户信息
    ['qq_nick', 'qq_head'].forEach((key) => {
      cookie.remove(key, {
        domain: 'film.video.qq.com',
        path: '/',
      });
    });
    // 同步用户头像昵称，用于展示用
    cookie.set('qq_nick', vqqNick, cookieOption);
    cookie.set('qq_head', vqqHead, cookieOption);
    setReady();
  });
}

const maxTokenRefreshingAge = 5 * 60 * 1000; // 5分钟
/**
 * 是否需要刷新票据
 * 1. 距上次刷新超过5分钟
 * 2. cookie中没有昵称或头像信息
 * 3. 与上次刷新的vuserid不一致
 * @returns {Boolean}
 */
function needRefreshAuthToken() {
  const lastRefreshTime = Number(cookie.get('last_refresh_time'));
  const lastVuserid = cookie.get('last_refresh_vuserid');

  if (Date.now() - lastRefreshTime >= maxTokenRefreshingAge || !getNick() || !getAvatar()
    || lastVuserid !== getVuid()) {
    return true;
  }
  return false;
}

/**
 * 监听初始化完成
 * @param {Function} callback 回调函数
 */
function onInit(callback) {
  if (isInitReady) {
    typeof callback === 'function' && callback();
  } else {
    ONREADY_QUEUES.push(callback);
  }
}

function syncAppCookies() {
  return invoke('getMainCookie').then((res) => {
    let cookies = {};
    if (res && res.cookie) {
      cookies = parseCookieString(res.cookie);
      cookies.main_login = res.type;
      if (res.type === LOGIN_TYPE.wx) { // 微信账号添加vusession/vuserid到cookie中
        if (!hasOwn(cookies, 'vuserid') && res.vuserid) {
          cookies.vuserid = res.vuserid;
        }
        if (!hasOwn(cookies, 'vusession') && res.vusession) {
          cookies.vusession = res.vusession;
        }
      }
    }
    cookie.setCookies(cookies);
  })
    .catch((e) => {
      console.log('[common/login.syncAppCookies]getMainCookie failed in local pkg mode', e);
      cookie.setCookies();
    });
}

export {
  buildCSRF,
  checkFamilyChildLogin,
  checkUser,
  getFamilyRole,
  getLoginInfo,
  getMainCookie,
  getMainUserInfo,
  getUid,
  getUin,
  getVuid,
  isFamilyChildLogin,
  isLogin,
  isNewQQLogin,
  isPhoneLogin,
  isPTLogin,
  isQQLogin,
  isStrictLogin,
  isWXLogin,
  logout,
  openLogin,
};

// public function
const DEFAULT_LOGIN = {
  VERSION: LOGIN_VERSION,
  setConfig,
  init,
  onInit,
  loginType,
  isLogin,
  isStrictLogin,
  isPTLogin,
  isNewQQLogin,
  isQQLogin,
  isWXLogin,
  isPhoneLogin,
  openLogin,
  logout,
  checkUser,
  checkFamilyChildLogin,
  getMainUserInfo,
  getMainCookie,
  getLoginInfo,
  isFamilyChildLogin,
  getFamilyRole,
  getUin,
  getUid,
  getVuid,
  getToken,
  buildCSRF,
  source: 'vip',
};

export default new LoginProxy(DEFAULT_LOGIN);
